import { BaseInput, InputField, ProviderAccountRole, SelectField, ToastContext } from '@enter/core';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useMemo } from 'react';
import { RootStore, SettingsStore, IInjectedRootStore } from 'stores';
import * as Yup from 'yup';
import * as S from './invite-member-dialog.styles';

interface IInviteMemberDialogProps extends IInjectedRootStore {
    accountId: string | undefined;
    onClose: () => void;
}

interface IInvitation {
    email: string;
    roles: ProviderAccountRole[];
}

const maxLength = 170;

const schema = Yup.object().shape({
    email: Yup.string().required('Email is required'),
    roles: Yup.array<ProviderAccountRole>().min(1, 'Role is required').nullable(),
});

const getDisplayRoles = (role: string | undefined) => {
    if (!role) {
        return role;
    }

    const rolesMap = {
        ['PROVIDER_ADMIN_ACCESS']: 'Admin',
        ['PROVIDER_READ_ACCESS']: 'Read Only',
        ['PROVIDER_READ_WRITE_ACCESS']: 'Read and Write',
    };

    return rolesMap[role] || role;
};

export const InviteMemberDialog: React.FunctionComponent<IInviteMemberDialogProps> = inject('rootStore')(
    observer(
        (props: IInviteMemberDialogProps): React.ReactElement => {
            const { onClose, rootStore } = props;
            const context = useContext(ToastContext);

            if (!rootStore) {
                return <></>;
            }
            const settingsStore = rootStore.settingsStore;

            const handleCancel = (): void => {
                onClose && onClose();
            };
            const handleSubmit = async (values: IInvitation, { setSubmitting }: FormikActions<any>): Promise<void> => {
                setSubmitting(true);
                if (settingsStore) {
                    await settingsStore.inviteMember(
                        values.email,
                        values.roles.filter(el => el.roleId),
                    );
                    context.notify && context.notify('Invitation sent');
                    onClose && onClose();
                }
                setSubmitting(false);
            };

            const accounts = rootStore.account ? _.compact(rootStore.account.accountAccess) : [];
            const roles = settingsStore.roles || [];

            const initialValues: IInvitation = useMemo(() => {
                return {
                    email: '',
                    roles: accounts.map(account => {
                        // const accountRole = _.find(user?.roles, role => role.accountId === account.accountId);
                        return {
                            accountId: account.accountId,
                            roleId: '',
                        };
                    }),
                };
            }, [accounts]);

            return (
                <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
                    {(formik: FormikProps<IInvitation>): React.ReactElement => (
                        <S.DialogBody>
                            <S.InputGroup>
                                <S.Label>Email</S.Label>
                                <S.FormField name="email" maxLength={maxLength} component={InputField} />
                                <S.Label>Roles</S.Label>
                                {accounts.map((account, i) => (
                                    <S.SubCategoryRow key={account.accountId}>
                                        <S.SubCategoryCol>
                                            <S.InputGroup>
                                                <S.InputWrapper>
                                                    <BaseInput value={account.name} disabled={true} />
                                                </S.InputWrapper>
                                            </S.InputGroup>
                                        </S.SubCategoryCol>
                                        <S.SubCategoryCol>
                                            <S.InputGroup>
                                                {/* <S.Label>Role</S.Label> */}
                                                <S.FormField
                                                    name={`roles[${i}].roleId`}
                                                    component={SelectField}
                                                    options={roles.map(role => {
                                                        return {
                                                            value: role.roleId,
                                                            label: getDisplayRoles(role.roleId),
                                                        };
                                                    })}
                                                />
                                            </S.InputGroup>
                                        </S.SubCategoryCol>
                                    </S.SubCategoryRow>
                                ))}
                            </S.InputGroup>
                            <S.ButtonGroup
                                changed={true}
                                useNotify={false}
                                onCancel={handleCancel}
                                onSave={formik.submitForm}
                                saveLabel="Send Invitation"
                                saveNotice="Invitation sent"
                            />
                        </S.DialogBody>
                    )}
                </Formik>
            );
        },
    ),
);
